<template>
  <div class="mt-8" v-if="loading">
    <loading />
  </div>
  <div class="w-full" v-else>
    <div class="p-4">
      <h1 class="mb-4">{{ $t("home.hi") + " " + user.username }}!</h1>
      <div
        class="mb-4 mx-4 py-2 bg-blue-100 border border-blue-500 text-blue-900 rounded"
        v-if="targetInvitations && targetInvitations.length > 0 || friendRequests && friendRequests.length > 0"
      >
        <div
          class="font-medium px-4 inline-block rounded-full cursor-pointer hover:text-black"
          v-if="targetInvitations && targetInvitations.length > 0"
          @click="goTo('/targets')"
        >
          <icon class="-mt-1" name="Target" />
          {{ $tc("dashboard.you_have_open_target_invitations", targetInvitations.length, {count: targetInvitations.length}) }}
        </div>
        <div
          class="w-20 ml-3 h-0 my-1 border-b border-blue-300"
          v-if="targetInvitations && targetInvitations.length > 0 && friendRequests && friendRequests.length > 0"
        />
        <div
          class="font-medium px-4 inline-block rounded-full cursor-pointer hover:text-black"
          v-if="friendRequests && friendRequests.length > 0"
          @click="goTo('/friends')"
        >
          <icon class="-mt-1" name="Contacts" />
          {{ $tc("dashboard.you_have_pending_friend_requests", friendRequests.length, {count: friendRequests.length}) }}
        </div>
      </div>
      <span>{{ $t("dashboard.your_achievements") }}</span>
    </div>
    <div>
      <tabs bright popped css="bg-white">
        <tab @input="updateTab(0)" bright popped :active="activeTab === 0">
          {{
          $t("general.today")
          }}
        </tab>
        <tab @input="updateTab(1)" bright popped :active="activeTab === 1">
          {{
          $t("dashboard.history.history")
          }}
        </tab>
      </tabs>
    </div>
    <div>
      <div v-if="activeTab === 0">
        <div class="w-48 mx-auto mt-8">
          <info-circle :items="activityCategories" inactive-color="#cccccc" radius="30" />
        </div>
        <div class="mt-2 w-full flex justify-center" v-if="activityCategories">
          <ul>
            <li
              class="leading-snug text-gray-700 cursor-pointer tracking-wide"
              v-for="category in activityCategories"
              :key="category.id"
              @click="openCategoryActivities(category.id)"
            >
              <span class="mr-2" :style="{ color: category.color }">
                <icon :name="category.icon" />
              </span>
              <span
                :style="{ color: category.active ? category.color : 'inherit' }"
              >{{ category.title }}</span>
            </li>
          </ul>
        </div>
      </div>
      <div v-if="activeTab === 1">
        <div class="p-4 max-w-sm mx-auto">
          <input-select
            :label="$t('dashboard.history.period')"
            :options="pastOptions"
            optionlabel="title"
            optionvalue="value"
            value="week"
            @update-value="activePast = $event"
          />
        </div>
        <div v-if="activePast === 'week'">
          <div class="p-4">
            <h3 class="text-center mb-4">{{ $t("dashboard.your_active_days_week") }}</h3>
            <div class="mb-2 flex justify-around">
              <activity-week
                clickable
                :days="
                entriesWeek
                  ? entriesWeek
                      .filter((e) => checkDate(e.created_at, -8))
                      .map((e) => e.created_at)
                  : []
              "
              />
            </div>
          </div>
          <h3 class="text-center mb-4">{{ $t("dashboard.your_last_seven_days") }}</h3>
          <div class="pt-0 w-full flex justify-center">
            <ul class="list-none">
              <li>
                <span class="mr-2 font-medium">{{ entriesDays.length }}</span>
                {{
                $tc("activities.activity_done", entriesDays.length)
                }}
              </li>
              <li>
                <span class="mr-2 font-medium">{{ newFriendshipsWeek.length }}</span>
                {{ $t("dashboard.new_friends") }}
              </li>
              <li>
                <span class="mr-2 font-medium">{{ createdTargetsWeek.length }}</span>
                {{
                $tc("targets.target_created", createdTargetsWeek.length)
                }}
              </li>
              <li>
                <span class="mr-2 font-medium">{{ finishedTargetsWeek.length }}</span>
                {{
                $tc("targets.target_finished", finishedTargetsWeek.length)
                }}
              </li>
            </ul>
          </div>
        </div>
        <div v-if="activePast === 'month'">
          <div class="p-4">
            <h3 class="text-center mb-4">{{ $t("dashboard.your_active_days_month") }}</h3>
            <statistic-entries :entries="entriesMonth.grouped" :range="30" type="day" key="month" />
          </div>
          <h3 class="text-center mb-4">{{ $t("dashboard.your_last_month") }}</h3>
          <div class="pt-0 w-full flex justify-center">
            <ul class="list-none">
              <li>
                <span class="mr-2 font-medium">{{ entriesMonth.total }}</span>
                {{
                $tc("activities.activity_done", entriesMonth.total)
                }}
              </li>
              <li>
                <span class="mr-2 font-medium">{{ newFriendshipsMonth.length }}</span>
                {{ $t("dashboard.new_friends") }}
              </li>
              <li>
                <span class="mr-2 font-medium">{{ createdTargetsMonth.length }}</span>
                {{
                $tc("targets.target_created", createdTargetsMonth.length)
                }}
              </li>
              <li>
                <span class="mr-2 font-medium">{{ finishedTargetsMonth.length }}</span>
                {{
                $tc("targets.target_finished", finishedTargetsMonth.length)
                }}
              </li>
            </ul>
          </div>
        </div>
        <div v-if="activePast === 'year'">
          <div class="p-4">
            <h3 class="text-center mb-4">{{ $t("dashboard.your_active_weeks") }}</h3>
            <statistic-entries :entries="entriesYear.grouped" :range="365" type="week" key="year" />
          </div>
          <h3 class="text-center mb-4">{{ $t("dashboard.your_last_year") }}</h3>
          <div class="pt-0 w-full flex justify-center">
            <ul class="list-none">
              <li>
                <span class="mr-2 font-medium">{{ entriesYear.total }}</span>
                {{
                $tc("activities.activity_done", entriesYear.total)
                }}
              </li>
              <li>
                <span class="mr-2 font-medium">{{ newFriendshipsYear.length }}</span>
                {{ $t("dashboard.new_friends") }}
              </li>
              <li>
                <span class="mr-2 font-medium">{{ createdTargetsYear.length }}</span>
                {{
                $tc("targets.target_created", createdTargetsYear.length)
                }}
              </li>
              <li>
                <span class="mr-2 font-medium">{{ finishedTargetsYear.length }}</span>
                {{
                $tc("targets.target_finished", finishedTargetsYear.length)
                }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div class="p-8 max-w-md mx-auto">
      <info toggleable :title="$t('dashboard.info_title')">{{ $t('dashboard.info_message') }}</info>
    </div>
  </div>
</template>

<script>
const ActivityWeek = require("@/modules/activities/components/ActivityWeek.vue")
  .default;
const Tabs = require("@/modules/core/components/Tabs.vue").default;
const Tab = require("@/modules/core/components/Tab.vue").default;
const InfoCircle = require("@/modules/home/components/InfoCircle.vue").default;
const Info = require("@/modules/core/components/Info.vue").default;
const InputSelect = require("@/modules/input/components/InputSelect.vue")
  .default;
const StatisticEntries = require("@/modules/home/components/StatisticEntries.vue")
  .default;

import DateMixin from "@/mixins/DateMixin";

export default {
  name: "DashboardSite",
  mixins: [DateMixin],
  data() {
    return {
      user: null,
      activeTab: 0,
      entriesDays: [],
      entriesWeek: [],
      entriesMonth: [],
      entriesYear: [],
      activityCategories: [],
      newFriendshipsWeek: null,
      newFriendshipsMonth: null,
      newFriendshipsYear: null,
      finishedTargetsWeek: [],
      finishedTargetsMonth: [],
      finishedTargetsYear: [],
      createdTargetsWeek: [],
      createdTargetsMonth: [],
      createdTargetsYear: [],
      activePast: "week",
      pastOptions: [
        {
          title: this.$t("dashboard.history.week"),
          value: "week"
        },
        {
          title: this.$t("dashboard.history.month"),
          value: "month"
        },
        {
          title: this.$t("dashboard.history.year"),
          value: "year"
        }
      ]
    };
  },
  props: {},
  components: {
    Tabs,
    Tab,
    ActivityWeek,
    InfoCircle,
    Info,
    InputSelect,
    StatisticEntries
  },
  methods: {
    updateTab(Index) {
      this.activeTab = Index;
    },
    openCategoryActivities(CategoryId) {
      this.goTo({
        path: "/activities",
        query: { category: CategoryId }
      });
    }
  },
  computed: {
    friendRequests() {
      return this.$store.getters["userfriendrequest/incomingFriendRequests"];
    },
    targetInvitations() {
      return this.$store.getters["targetinvitation/targetInvitations"];
    }
  },
  async created() {
    this.user = this.$store.getters["auth/user"];

    //──── Get activity categories ───────────────────────────────────────────────────────────
    this.activityCategories = await this.$store.getters[
      "activitycategory/api"
    ].GetAll();

    //──── Get new friendships ───────────────────────────────────────────────────────────────
    this.newFriendshipsWeek = await this.$store.getters[
      "userfriend/api"
    ].Custom.Period(7);

    //──── Get new friendships ───────────────────────────────────────────────────────────────
    this.newFriendshipsMonth = await this.$store.getters[
      "userfriend/api"
    ].Custom.Period(30);

    //──── Get new friendships ───────────────────────────────────────────────────────────────
    this.newFriendshipsYear = await this.$store.getters[
      "userfriend/api"
    ].Custom.Period(365);

    //──── Get open incoming friendRequests ───────────────────────────────────────────────────────────────
    await this.$store.getters["userfriendrequest/api"].Custom.Incoming();

    //──── Get last 7 days entries ────────────────────────────────────────────────────────────
    this.entriesDays = await this.$store.getters[
      "activityentry/api"
    ].Custom.Period(7);

    //──── Get this weeks entries ────────────────────────────────────────────────────────────
    this.entriesWeek = await this.$store.getters[
      "activityentry/api"
    ].Custom.ThisWeek();

    //──── Get this weeks entries ────────────────────────────────────────────────────────────
    this.entriesMonth = await this.$store.getters[
      "activityentry/api"
    ].Custom.MyStats({ range: 30, type: "daily" });

    //──── Get this weeks entries ────────────────────────────────────────────────────────────
    this.entriesYear = await this.$store.getters[
      "activityentry/api"
    ].Custom.MyStats({ range: 365, type: "weekly" });

    //──── Get my last weeks finished targets  ──────────────────────────────────────────────────────
    this.finishedTargetsWeek = await this.$store.getters[
      "target/api"
    ].Custom.FinishedPeriod(7);

    //──── Get my last weeks finished targets  ──────────────────────────────────────────────────────
    this.finishedTargetsMonth = await this.$store.getters[
      "target/api"
    ].Custom.FinishedPeriod(30);

    //──── Get my last weeks finished targets  ──────────────────────────────────────────────────────
    this.finishedTargetsYear = await this.$store.getters[
      "target/api"
    ].Custom.FinishedPeriod(365);

    //──── Get my last weeks finished targets  ──────────────────────────────────────────────────────
    await this.$store.getters["targetinvitation/api"].Custom.My();

    //──── Get my last weeks created targets  ──────────────────────────────────────────────────────
    this.createdTargetsWeek = await this.$store.getters[
      "target/api"
    ].Custom.CreatedPeriod(7);

    //──── Get my last weeks created targets  ──────────────────────────────────────────────────────
    this.createdTargetsMonth = await this.$store.getters[
      "target/api"
    ].Custom.CreatedPeriod(30);

    //──── Get my last weeks created targets  ──────────────────────────────────────────────────────
    this.createdTargetsYear = await this.$store.getters[
      "target/api"
    ].Custom.CreatedPeriod(365);

    //──── Get last weeks entries ────────────────────────────────────────────────────────────
    await this.$store.getters["activity/api"].Custom.AllMy();

    let todaysEntries = this.entriesWeek.filter(e =>
      this.isToday(e.created_at)
    );

    //──── Look if categories have entries ─────────────────────────────────────────────────
    let categoryEntries = new Set(
      todaysEntries.map(e => {
        return this.$store.getters["activity/activityCategoryId"](
          e.activity_id
        );
      })
    );

    this.activityCategories.forEach(c => {
      c.active = categoryEntries.has(c.id);
    });

    this.loaded();
  }
};
</script>
